import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { C, fmtINR } from "../constants";
import { IPD_COLORS } from "../../ipd/utils/ipdColors";
import { useBillingPermissions } from "../useBillingPermissions";
import { BILLING_PERMS } from "../billing-api";
import { systemCashOf } from "../useCashCounter";
import IpdSectionCard from "../../ipd/utils/IpdSectionCard";
import IpdTextField from "../../ipd/utils/IpdTextField";
import IpdFormField from "../../ipd/utils/IpdFormField";
import IpdButton from "../../ipd/utils/IpdButton";
import SvgIcon from "../SvgIcon";
import { ErrorBar, ModeTotals, StatTile } from "./CashCounterUI";

// ─────────────────────────────────────────────────────────────────────────────
// Screen 3 — Final Scroll Review (two steps, one reconciliation).
//   1. Generate Final Scroll (G5) — POST scrolls/final/ needs declared_cash_amount,
//      so declared cash is entered HERE, not only at finalize.
//   2. Finalize & Close Day (G6) — POST cash-scrolls/{scroll_id}/finalize/.
// Declared cash + remarks are captured once and reused across both calls.
// ─────────────────────────────────────────────────────────────────────────────

const draftKey = (id) => `ipd_cash_counter_draft_${id}`;

export default function FinalScrollReview({ cc, onBack, onClosed }) {
  const { can } = useBillingPermissions();
  const canFinalView = can(BILLING_PERMS.SCROLL_FINAL_VIEW);
  const canFinalClose = can(BILLING_PERMS.SCROLL_FINAL_CLOSE);
  const canFinalPrint = can(BILLING_PERMS.SCROLL_FINAL_PRINT);

  const { session, scroll, finalScrollId, busy, error, canRetry, retry } = cc;
  const systemCash = systemCashOf(scroll);

  const [declared, setDeclared] = useState("");
  const [remarks, setRemarks] = useState("");
  const [localError, setLocalError] = useState("");

  const alreadyClosed = session?.status === "CLOSED";
  const hasScroll = Boolean(finalScrollId);

  // Interim totals (needed for the variance) auto-load in the hook when the
  // session is open — no fetch needed here.

  // Seed declared cash from the system figure (and restore any draft) once, without
  // clobbering edits the user has already made.
  useEffect(() => {
    const key = finalScrollId || session?.id;
    let draft = null;
    if (key) { try { draft = JSON.parse(sessionStorage.getItem(draftKey(key))); } catch { /* ignore */ } }
    setDeclared((cur) => cur || draft?.declared || (systemCash ? String(systemCash) : ""));
    setRemarks((cur) => cur || draft?.remarks || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalScrollId, session?.id, systemCash]);

  const variance = Number(declared || 0) - Number(systemCash || 0);
  const balanced = Math.abs(variance) < 0.01;

  const saveDraft = () => {
    const key = finalScrollId || session?.id;
    if (!key) return;
    try {
      sessionStorage.setItem(draftKey(key), JSON.stringify({ declared, remarks }));
      toast.success("Draft saved on this device.");
    } catch {
      toast.error("Could not save the draft — browser storage is unavailable.");
    }
  };

  const guard = () => {
    setLocalError("");
    if (declared === "" || Number.isNaN(Number(declared))) {
      setLocalError("Enter the declared cash amount.");
      return false;
    }
    if (!balanced && !remarks.trim()) {
      setLocalError("Explain the variance before continuing.");
      return false;
    }
    return true;
  };

  const doGenerate = async () => {
    if (!session?.id || alreadyClosed || !guard()) return;
    await cc.generateFinal({ declaredCash: declared, remarks: remarks.trim() || undefined });
    // On success finalScrollId is set → the Finalize button appears below.
  };

  const doFinalize = async () => {
    if (!finalScrollId || alreadyClosed || !guard()) return;
    const r = await cc.finalize({ declaredCash: declared, closingRemarks: remarks.trim() || undefined });
    if (r.ok) {
      try { sessionStorage.removeItem(draftKey(finalScrollId)); } catch { /* ignore */ }
      if (onClosed) onClosed();
    }
  };

  const noSession = !session?.id;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" }}>
        <IpdButton
          variant="outlined"
          onClick={onBack}
          startIcon={<SvgIcon name="back" size={15} color={C.textSub} />}
          sx={{ color: C.textSub, backgroundColor: "transparent", borderColor: IPD_COLORS.border, "&:hover": { backgroundColor: IPD_COLORS.selectedBg } }}
        >
          Back
        </IpdButton>
        {canFinalPrint && hasScroll && (
          <IpdButton
            variant="outlined"
            onClick={() =>
              cc.printFinal({
                systemCash,
                declaredCash: Number(declared || 0),
                variance,
                remarks: remarks.trim(),
              })
            }
            disabled={busy || !scroll}
            startIcon={<SvgIcon name="printer" size={15} color={C.text} />}
          >
            Print Final Scroll
          </IpdButton>
        )}
      </div>

      <ErrorBar message={error} onRetry={canRetry ? retry : null} busy={busy} />

      {noSession && (
        <div style={{ background: IPD_COLORS.warning.bg, border: `1px solid ${IPD_COLORS.warning.border}`, borderRadius: "8px", padding: "10px 14px", fontSize: "12px", color: IPD_COLORS.warning.text, fontWeight: "600" }}>
          No open session — open a counter session first.
        </div>
      )}
      {alreadyClosed && (
        <div style={{ background: IPD_COLORS.pageBg, border: `1px solid ${C.border}`, borderRadius: "8px", padding: "10px 14px", fontSize: "12px", color: C.textSub, fontWeight: "600" }}>
          This session is already closed.
        </div>
      )}

      {/* ── System totals ──────────────────────────────────────────────────── */}
      <IpdSectionCard title="System totals" actions={
        <span style={{ fontSize: "11px", fontWeight: "700", color: hasScroll ? C.success : C.textSub, textTransform: "uppercase", letterSpacing: "0.05em" }}>
          {hasScroll ? "Scroll generated" : "Step 1 — generate"}
        </span>
      }>
        {!scroll ? (
          <p style={{ margin: 0, fontSize: "13px", color: C.textSub }}>Loading interim totals…</p>
        ) : (
          <>
            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: "16px" }}>
              <StatTile label="Receipts" value={fmtINR(scroll.receiptAmount)} sub={`${scroll.receiptCount} receipt${scroll.receiptCount === 1 ? "" : "s"}`} tone={C.success} />
              <StatTile label="Refunds" value={fmtINR(scroll.refundAmount)} sub={`${scroll.refundCount} refund${scroll.refundCount === 1 ? "" : "s"}`} tone={C.danger} />
              <StatTile label="Net collection" value={fmtINR(scroll.netCollection)} sub="Receipts − refunds" />
            </div>
            <div style={{ fontSize: "11px", fontWeight: "700", color: C.textSub, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "8px" }}>Mode split</div>
            <ModeTotals modes={scroll.modes} fmt={fmtINR} />
          </>
        )}
      </IpdSectionCard>

      {/* ── Reconciliation ─────────────────────────────────────────────────── */}
      <IpdSectionCard title="Cash reconciliation">
        <div style={{ display: "flex", gap: "14px", flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 200px" }}>
            <IpdFormField label="Expected cash (system)">
              <IpdTextField
                value={fmtINR(systemCash)}
                InputProps={{ readOnly: true }}
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": { bgcolor: IPD_COLORS.pageBg },
                  "& .MuiOutlinedInput-input": { fontWeight: 700 },
                }}
              />
            </IpdFormField>
          </div>
          <div style={{ flex: "1 1 200px" }}>
            <IpdFormField label="Declared cash (₹)">
              <IpdTextField
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
                value={declared}
                onChange={(e) => { setDeclared(e.target.value); setLocalError(""); }}
                disabled={noSession || alreadyClosed}
                fullWidth
                sx={{ "& .MuiOutlinedInput-input": { fontWeight: 700 } }}
              />
            </IpdFormField>
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", borderRadius: "8px", marginTop: "14px", background: balanced ? IPD_COLORS.success.bg : IPD_COLORS.warning.bg }}>
          <span style={{ fontSize: "12px", fontWeight: "700", color: C.textSub, textTransform: "uppercase", letterSpacing: "0.06em" }}>Variance</span>
          <span style={{ fontSize: "16px", fontWeight: "800", color: balanced ? C.success : IPD_COLORS.warning.text }}>{fmtINR(variance)}</span>
        </div>

        <div style={{ marginTop: "14px" }}>
          <IpdFormField label="Remarks" required={!balanced}>
            <IpdTextField
              multiline
              rows={3}
              value={remarks}
              disabled={noSession || alreadyClosed}
              onChange={(e) => { setRemarks(e.target.value); setLocalError(""); }}
              placeholder={balanced ? "Balanced" : "Why is there a difference?"}
              fullWidth
            />
          </IpdFormField>
        </div>

        {localError && <p style={{ margin: "10px 0 0", fontSize: "12px", color: C.danger, fontWeight: "600" }}>{localError}</p>}

        <div style={{ display: "flex", gap: "10px", marginTop: "18px", flexWrap: "wrap", alignItems: "center" }}>
          <IpdButton variant="outlined" onClick={saveDraft} disabled={busy || noSession}>Save Draft</IpdButton>

          {!hasScroll ? (
            canFinalView && (
              <IpdButton
                tone="teal"
                onClick={doGenerate}
                disabled={busy || noSession || alreadyClosed}
                startIcon={<SvgIcon name="ledger" size={15} color={IPD_COLORS.white} />}
              >
                {busy ? "Generating…" : "Generate Final Scroll"}
              </IpdButton>
            )
          ) : (
            canFinalClose && (
              <IpdButton
                tone="teal"
                onClick={doFinalize}
                disabled={busy || alreadyClosed}
                startIcon={<SvgIcon name="lock" size={15} color={IPD_COLORS.white} />}
              >
                {busy ? "Closing…" : "Finalize & Close Day"}
              </IpdButton>
            )
          )}

          {hasScroll && !alreadyClosed && (
            <span style={{ fontSize: "11px", color: C.textSub, fontWeight: "600" }}>
              Step 2 — reconcile &amp; close
            </span>
          )}
        </div>

        {!hasScroll && !canFinalView && (
          <p style={{ margin: "10px 0 0", fontSize: "11px", color: C.textSub }}>
            You do not have permission to generate the final scroll (<code>SCROLL_FINAL_VIEW</code>).
          </p>
        )}
        {hasScroll && !canFinalClose && (
          <p style={{ margin: "10px 0 0", fontSize: "11px", color: C.textSub }}>
            You do not have permission to close the day (<code>SCROLL_FINAL_CLOSE</code>).
          </p>
        )}
      </IpdSectionCard>
    </div>
  );
}
